// Structured Logging System - Issue #19
// Per-category log levels with browser console API

import { isObject } from './safeStorage'

export type LogLevel = 'off' | 'error' | 'warn' | 'info' | 'debug'
export type LogCategory = 'auth' | 'data' | 'stl' | 'ws' | 'ui' | 'net'

const LEVEL_ORDER: LogLevel[] = ['off', 'error', 'warn', 'info', 'debug']
const STORAGE_KEY = 'LOG_LEVELS'

const DEFAULTS: Record<LogCategory, LogLevel> = {
  auth: 'info',
  data: 'info',
  stl: 'info',
  ws: 'warn',
  ui: 'off',
  net: 'warn',
}

class LogManager {
  private levels: Record<LogCategory, LogLevel>

  constructor() {
    this.levels = { ...DEFAULTS }
    this.load()
  }

  private load(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (!stored) return
      const parsed: unknown = JSON.parse(stored)
      if (!isObject(parsed)) {
        localStorage.removeItem(STORAGE_KEY)
        return
      }
      for (const key of Object.keys(DEFAULTS) as LogCategory[]) {
        const value = parsed[key]
        if (typeof value === 'string' && (LEVEL_ORDER as string[]).includes(value)) {
          this.levels[key] = value as LogLevel
        }
      }
    } catch {
      try { localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
    }
  }

  private save(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.levels))
  }

  set(category: LogCategory, level: LogLevel): void {
    this.levels[category] = level
    this.save()
    console.log(`[LOG] ${category} → ${level}`)
  }

  all(level: LogLevel): void {
    for (const cat of Object.keys(this.levels) as LogCategory[]) {
      this.levels[cat] = level
    }
    this.save()
    console.log(`[LOG] All categories → ${level}`)
  }

  reset(): void {
    this.levels = { ...DEFAULTS }
    this.save()
    console.log('[LOG] Reset to defaults')
  }

  status(): void {
    console.table(this.levels)
  }

  private shouldLog(category: LogCategory, level: LogLevel): boolean {
    const catIdx = LEVEL_ORDER.indexOf(this.levels[category])
    const msgIdx = LEVEL_ORDER.indexOf(level)
    return catIdx > 0 && msgIdx > 0 && msgIdx <= catIdx
  }

  log(category: LogCategory, level: LogLevel, message: string, context?: Record<string, unknown>): void {
    if (!this.shouldLog(category, level)) return

    const ctxStr = context
      ? ' | ' + Object.entries(context).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' | ')
      : ''

    const msg = `[${category.toUpperCase()}:${level.toUpperCase()}] ${message}${ctxStr}`

    switch (level) {
      case 'error': console.error(msg); break
      case 'warn': console.warn(msg); break
      default: console.log(msg)
    }
  }
}

export const logManager = new LogManager()

// Expose to browser console
if (typeof window !== 'undefined') {
  (window as any).logs = {
    set: (cat: LogCategory, lvl: LogLevel) => logManager.set(cat, lvl),
    all: (lvl: LogLevel) => logManager.all(lvl),
    reset: () => logManager.reset(),
    status: () => logManager.status(),
    help: () => console.log(
      'logs.set(category, level) - Set level for category\n' +
      'logs.all(level)           - Set all categories\n' +
      'logs.reset()              - Reset to defaults\n' +
      'logs.status()             - Show current levels\n' +
      'Categories: auth, data, stl, ws, ui, net\n' +
      'Levels: off, error, warn, info, debug'
    ),
  }
}

// Convenience: log.data('info', 'msg', {key: val})
type CategoryLogger = (level: LogLevel, msg: string, ctx?: Record<string, unknown>) => void

function catLogger(cat: LogCategory): CategoryLogger {
  return (level, msg, ctx) => logManager.log(cat, level, msg, ctx)
}

export const log = {
  auth: catLogger('auth'),
  data: catLogger('data'),
  stl:  catLogger('stl'),
  ws:   catLogger('ws'),
  ui:   catLogger('ui'),
  net:  catLogger('net'),
}

